/**
 * Tools function
 * @authors Luo-jinghui (luojinghui424@gmail.com)
 * @date  2019-11-08 15:54:21
 */

import { XYReturnResult, ITemplateObj } from '../type';
import { TempValType } from '../type/enum.type';
import { SERVER_ERROR_MAP, SDK_ERROR_MAP, SDK_ERROR_MAP_V2 } from './error';
import { locale } from './i18n';

/**
 * 节流函数
 *
 * @param fn event function
 * @param delay 每隔多久必须触发一次
 * @param atleast 最短间隔触发时间
 */
export const debounce = function (fn: any, delay: number, atleast: number) {
  let timer = null;
  let previous = null;

  return function () {
    const now = +new Date();
    const context = this;
    const args = arguments;

    clearTimeout(timer);
    if (!previous) {
      previous = now;
    }

    if (now - previous >= atleast) {
      fn.apply(context, args);
      // 重置上一次开始时间为本次结束时间
      previous = now;
    } else {
      timer = setTimeout(function () {
        fn.apply(context, args);
      }, delay);
    }
  };
};

/**
 * 防抖函数
 *
 * @param fn Event function
 * @param wait 等待多少毫秒触发
 */
export const throttle = function (fn: any, wait: number) {
  let lastTime = 0;
  let timer = null;

  return function () {
    const nowTime = +new Date();
    const context = this;
    const args = arguments;

    if (!timer) {
      timer = setTimeout(() => {
        fn.apply(context, args);
        clearTimeout(timer);
        timer = null;
      }, wait);
    }
  };
};

/**
 * 获取当前时间
 *
 * Format：xxxx-xx-xx xx:xx
 */
export const currentTime = () => {
  const now = new Date();
  const year = now.getFullYear(); //年
  const month = now.getMonth() + 1; //月
  const day = now.getDate(); //日
  const hh = now.getHours(); //时
  const mm = now.getMinutes(); //分
  let clock = year + '-';

  if (month < 10) clock += '0';
  clock += month + '-';

  if (day < 10) clock += '0';
  clock += day + ' ';

  if (hh < 10) clock += '0';
  clock += hh + ':';

  if (mm < 10) clock += '0';
  clock += mm;

  return clock;
};

/**
 * 获取当前时间
 *
 * Format：xx-xx xx:xx:xx.xx
 */
export const getTime = () => {
  const now = new Date();
  const month = now.getMonth() + 1; //月
  const day = now.getDate(); //日
  const hh = now.getHours(); //时
  const mm = now.getMinutes(); //分
  const ss = now.getSeconds(); // 秒
  const ms = now.getMilliseconds(); // 毫秒
  let clock = '';

  if (month < 10) clock += '0';
  clock += month + '-';

  if (day < 10) clock += '0';
  clock += day + ' ';

  if (hh < 10) clock += '0';
  clock += hh + ':';

  if (mm < 10) clock += '0';
  clock += mm + ':';

  if (ss < 10) clock += '0';
  clock += ss + '.';

  clock += ms;

  return clock;
};

/**
 * 获取当前时间
 *
 * Format：xx:xx:xx xx
 */
export const getMillTime = () => {
  const date2 = new Date();
  const nowTime = `${date2.getHours()}:${date2.getMinutes()}:${date2.getSeconds()} ${date2.getMilliseconds()}`;

  return nowTime;
};

/**
 * 错误信息格式化
 *
 * @param { string } sdk38code 错误码
 * @param { Record<string, any> } data 数据
 * @param { string } msg 消息描述
 * @returns { XYReturnResult } 错误码数据
 */
export const getStatus = (
  sdk38code: string,
  data: Record<string, any> = {},
  msg = ''
) => {
  const code = SDK_ERROR_MAP_V2[sdk38code] ?? sdk38code;
  return {
    code,
    msg: msg || SERVER_ERROR_MAP[locale][code] || SDK_ERROR_MAP[locale][code],
    data,
  };
};

/**
 * 判断值类型
 */
export const TYPEOF = {
  isNumber(num: any) {
    return typeof num === 'number';
  },
  isObject(obj: any) {
    return Object.prototype.toString.call(obj).toLocaleLowerCase() === '[object object]';
  },
  isArray(arr: any) {
    return Array.isArray(arr);
  },
  isString(str: any) {
    return typeof str === 'string';
  },
  isBoolean(str: any) {
    return typeof str === 'boolean';
  },
  isUndefined(str: any) {
    return typeof str === 'undefined';
  },
  isFunction(val: any): val is Function {
    return typeof val === 'function';
  },
  // 是否是字母
  isLetter(val: any) {
    const regex = /^[a-zA-Z]+$/;
    return regex.test(val);
  },
  // 是否是汉字
  isChinese(val: any) {
    const regex = /^[\u4e00-\u9fa5]+$/;
    return regex.test(val);
  },
  // 是否是数字，包含数字字符串
  isNumberText(val: any) {
    const regex = /^[0-9]+$/;
    return regex.test(val);
  },
  // 是否是特殊符号
  isSpecialChar(val: any) {
    const regex = /^[^\u4e00-\u9fa5a-zA-Z0-9]+$/;
    return regex.test(val);
  },
  // 是否是空格
  isWhiteSpace(val: any) {
    const regex = /^\s+$/;
    return regex.test(val);
  }
};


/**
 * 将演讲者模式转化成content缩略视图模式
 * 因为content模式中可能存在主会场，此时需要将P1替换为主会场，因此标注一下P1
 *
 * @param {ITemplateObj} SPEAKER 演讲者模板
 * @returns {ITemplateObj}
 */
export const toMultiPicContentHighPriority = (SPEAKER: ITemplateObj) => {
  const tempInfo = JSON.parse(JSON.stringify(SPEAKER));

  Object.keys(tempInfo.temp).forEach((key) => {
    const tmpArr = tempInfo.temp[key];

    if (tmpArr.length < 2) {
      return;
    }
    // 第一个设置位content
    tmpArr[1].resolution = 4;
    tmpArr[1].type = TempValType.CONTENT;

    // p2变成P1
    if (tmpArr[2]) {
      tmpArr[2].type = TempValType.P1; // content模式下存在将主会场放在p1位置上的情况
    }
  });

  return tempInfo;
};

/**
 * 将演讲者模式转化成content缩略共享模式
 *
 * @param SPEAKER 演讲者模板
 * @returns {ITemplateObj}
 */
export const toMultiPicActiveHighPriority = (SPEAKER: ITemplateObj) => {
  const tempInfo = JSON.parse(JSON.stringify(SPEAKER));

  Object.keys(tempInfo.temp).forEach((key) => {
    const tmpArr = tempInfo.temp[key];

    if (tmpArr.length < 2) {
      return;
    }

    if (tmpArr.length === 2) {
      // 将local变为content
      tmpArr[0].resolution = 3;
      tmpArr[0].type = TempValType.CONTENT;

      // 第p1位变为local
      tmpArr[1].resolution = 1;
      tmpArr[1].type = TempValType.LOCAL;

      // 要保证第一个是local, 方便渲染，应该优化一下
      [tmpArr[0], tmpArr[1]] = [tmpArr[1], tmpArr[0]];
    } else {
      // 将p2变为content
      tmpArr[2].resolution = 3;
      tmpArr[2].type = TempValType.CONTENT;

      // 标注P1 不需要了，模板里已经存在了
      // tmpArr[1].type = 'P1';
      [tmpArr[1], tmpArr[2]] = [tmpArr[2], tmpArr[1]]; // 优先级 local > content > 主会场 > 其它
    }
  });

  return tempInfo;
};

/**
 * 将字符串转为16进制
 *
 * @param {string} str
 * @returns {string}
 */
export const stringToHex = (str) => {
  str = new TextEncoder().encode(decodeURI(str));
  let val = '';
  for (let i = 0; i < str.length; i++) {
    if (val == '') val = str[i].toString(16);
    else val += str[i].toString(16);
  }
  return val;
};

/**
 * argb图片转rgba
 *
 * @param {ArrayBuffer} src argb array buffer
 * @param {boolean} convertAlpha 是否转换 alpha 通道，默认：false（不转换）
 * @returns {Uint8Array} 转换后的 rgba 数据
 */
export const argbToRgba = (src: ArrayBuffer, convertAlpha = false) => {
  const uint8Arr = new Uint8Array(src);
  for (let i = 0, len = uint8Arr.byteLength; i < len; i += 4) {
    const b = uint8Arr[i];
    const g = uint8Arr[i + 1];
    const r = uint8Arr[i + 2];
    const a = uint8Arr[i + 3];
    uint8Arr[i] = r;
    uint8Arr[i + 1] = g;
    uint8Arr[i + 2] = b;
    uint8Arr[i + 3] = convertAlpha ? a || 255 : a;
  }
  return uint8Arr;
};

/**
 * 检查图片是不是一张纯黑图片
 *
 * @param {ArrayBuffer} buffer 图片数据
 * @returns {boolean}
 */
export const isBlackImg = (buffer: ArrayBuffer) => {
  const uint8 = new Uint8Array(buffer);
  const len = uint8.byteLength;

  const checkRange = (start: number, end: number) => {
    for (let i = start; i < end; i += 4) {
      const c1 = uint8[i],
        c2 = uint8[i + 1],
        c3 = uint8[i + 2];

      if (c1 !== 0 || c2 !== 0 || c3 !== 0) return false;
    }
    return true;
  };

  // 检查前 100 个像素
  if (checkRange(0, 400)) {
    // 取出中间的那个像素索引
    const middle = Math.floor(len / 8) * 4;
    return checkRange(middle, middle + 200) && checkRange(len - 200, len);
  }
  return false;
};

/**
 * 获取n-ua信息
 *
 * @returns {string}
 */
export const getNuaInfo = (appType?: string) => {
  const version = 'SDK_PKG_VERSION';
  const versionParts = version.split('-');
  const AV = versionParts[0];
  const DR = versionParts[1] || '';
  const screenWidth = window.screen.width;
  const screenHeight = window.screen.height;
  const RL = `${screenWidth}*${screenHeight}`; // 分辨率
  const OS = process.platform || '';

  return `PL=${
    appType ?? 'electron_sdk'
  }&AV=${AV}&DR=${DR}&RL=${RL}&OS=${OS}&DE=Chrome`;
};
